// @ts-nocheck
import React from 'react';

export type BotIconType = 'ai-chip' | 'ai-assistant' | 'chat-bot' | 'sparkle-ai' | 'neural-net' | 'rocket' | 'custom';

interface BotIconProps {
  type?: BotIconType;
  customImage?: string; // URL or base64
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Modern Bot Icon Component
 * Professional AI-themed icons with cool designs
 */
const BotIcon: React.FC<BotIconProps> = ({
  type = 'ai-chip',
  customImage,
  size = 28,
  className = '',
  style = {}
}) => {

  // Custom uploaded image
  if (type === 'custom' && customImage) {
    return (
      <img
        src={customImage}
        alt="Bot"
        className={`bot-icon ${className}`}
        style={{
          width: size,
          height: size,
          objectFit: 'contain',
          borderRadius: '6px',
          ...style
        }}
      />
    );
  }

  // Modern SVG Icons - Cool, professional designs
  const icons: Record<BotIconType, JSX.Element> = {
    'ai-chip': (
      // Microchip AI - Modern tech look
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={style} className={className}>
        <rect x="8" y="8" width="16" height="16" rx="2" fill="url(#grad1)" stroke="#3068D4" strokeWidth="1.5"/>
        <path d="M12 12h8M12 16h8M12 20h8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="16" cy="16" r="2" fill="#fbbf24"/>
        <rect x="4" y="11" width="4" height="2" rx="1" fill="#667eea"/>
        <rect x="4" y="19" width="4" height="2" rx="1" fill="#667eea"/>
        <rect x="24" y="11" width="4" height="2" rx="1" fill="#667eea"/>
        <rect x="24" y="19" width="4" height="2" rx="1" fill="#667eea"/>
        <rect x="11" y="4" width="2" height="4" rx="1" fill="#667eea"/>
        <rect x="19" y="4" width="2" height="4" rx="1" fill="#667eea"/>
        <rect x="11" y="24" width="2" height="4" rx="1" fill="#667eea"/>
        <rect x="19" y="24" width="2" height="4" rx="1" fill="#667eea"/>
        <defs>
          <linearGradient id="grad1" x1="8" y1="8" x2="24" y2="24">
            <stop offset="0%" stopColor="#667eea"/>
            <stop offset="100%" stopColor="#764ba2"/>
          </linearGradient>
        </defs>
      </svg>
    ),

    'ai-assistant': (
      // Friendly AI Assistant
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={style} className={className}>
        <circle cx="16" cy="16" r="12" fill="url(#grad2)" stroke="#3068D4" strokeWidth="1.5"/>
        <circle cx="12" cy="14" r="2" fill="white"/>
        <circle cx="20" cy="14" r="2" fill="white"/>
        <path d="M12 20 Q16 23 20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        <path d="M8 10 L10 8 M24 10 L22 8" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round"/>
        <defs>
          <linearGradient id="grad2" x1="4" y1="4" x2="28" y2="28">
            <stop offset="0%" stopColor="#88B4FF"/>
            <stop offset="100%" stopColor="#4080F2"/>
          </linearGradient>
        </defs>
      </svg>
    ),

    'chat-bot': (
      // Speech Bubble Bot
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={style} className={className}>
        <rect x="4" y="6" width="24" height="18" rx="4" fill="url(#grad3)" stroke="#3068D4" strokeWidth="1.5"/>
        <path d="M12 24 L16 28 L16 24" fill="#4080F2"/>
        <circle cx="11" cy="15" r="2" fill="white"/>
        <circle cx="16" cy="15" r="2" fill="white"/>
        <circle cx="21" cy="15" r="2" fill="white"/>
        <defs>
          <linearGradient id="grad3" x1="4" y1="6" x2="28" y2="24">
            <stop offset="0%" stopColor="#4080F2"/>
            <stop offset="100%" stopColor="#2554B0"/>
          </linearGradient>
        </defs>
      </svg>
    ),

    'sparkle-ai': (
      // Sparkle Magic AI
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={style} className={className}>
        <path d="M16 4L17.5 11L24 12.5L17.5 14L16 21L14.5 14L8 12.5L14.5 11L16 4Z"
          fill="url(#grad4)" stroke="#d97706" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M24 6L25 9L28 10L25 11L24 14L23 11L20 10L23 9L24 6Z" fill="#fbbf24"/>
        <path d="M8 20L9 23L12 24L9 25L8 28L7 25L4 24L7 23L8 20Z" fill="#fbbf24"/>
        <circle cx="16" cy="13" r="2" fill="white" opacity="0.8"/>
        <defs>
          <linearGradient id="grad4" x1="8" y1="4" x2="24" y2="21">
            <stop offset="0%" stopColor="#f59e0b"/>
            <stop offset="100%" stopColor="#d97706"/>
          </linearGradient>
        </defs>
      </svg>
    ),

    'neural-net': (
      // Neural Network
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={style} className={className}>
        <circle cx="8" cy="8" r="3" fill="#4080F2" stroke="#3068D4" strokeWidth="1.5"/>
        <circle cx="24" cy="8" r="3" fill="#4080F2" stroke="#3068D4" strokeWidth="1.5"/>
        <circle cx="8" cy="24" r="3" fill="#4080F2" stroke="#3068D4" strokeWidth="1.5"/>
        <circle cx="24" cy="24" r="3" fill="#4080F2" stroke="#3068D4" strokeWidth="1.5"/>
        <circle cx="16" cy="16" r="4" fill="url(#grad5)" stroke="#2554B0" strokeWidth="1.5"/>
        <path d="M11 10 L14 14 M21 10 L18 14 M11 22 L14 18 M21 22 L18 18"
          stroke="#88B4FF" strokeWidth="1.5" strokeLinecap="round"/>
        <defs>
          <linearGradient id="grad5" x1="12" y1="12" x2="20" y2="20">
            <stop offset="0%" stopColor="#88B4FF"/>
            <stop offset="100%" stopColor="#4080F2"/>
          </linearGradient>
        </defs>
      </svg>
    ),

    'rocket': (
      // Rocket Launch
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={style} className={className}>
        <path d="M16 4 L20 12 L16 20 L12 12 Z" fill="url(#grad6)" stroke="#dc2626" strokeWidth="1.5"/>
        <ellipse cx="16" cy="12" rx="3" ry="4" fill="#fca5a5"/>
        <path d="M12 20 L10 26 L14 22" fill="#f97316"/>
        <path d="M20 20 L22 26 L18 22" fill="#f97316"/>
        <circle cx="16" cy="11" r="2" fill="white"/>
        <path d="M22 8 L26 6 M24 12 L28 12 M22 16 L26 18"
          stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round"/>
        <defs>
          <linearGradient id="grad6" x1="16" y1="4" x2="16" y2="20">
            <stop offset="0%" stopColor="#ef4444"/>
            <stop offset="100%" stopColor="#dc2626"/>
          </linearGradient>
        </defs>
      </svg>
    ),

    custom: <></>
  };

  return icons[type] || icons['ai-chip'];
};

export default BotIcon;

// Helper to get icon display name
export const getBotIconLabel = (type: BotIconType): string => {
  const labels: Record<BotIconType, string> = {
    'ai-chip': 'AI Chip',
    'ai-assistant': 'AI Assistant',
    'chat-bot': 'Chat Bot',
    'sparkle-ai': 'Sparkle AI',
    'neural-net': 'Neural Network',
    'rocket': 'Rocket',
    'custom': 'Custom Image'
  };
  return labels[type] || labels['ai-chip'];
};

// Get all icon types
export const getAllBotIconTypes = (): BotIconType[] => {
  return ['ai-chip', 'ai-assistant', 'chat-bot', 'sparkle-ai', 'neural-net', 'rocket'];
};
